import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { BookOpen, Download, FileText, Users, Printer, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  attivo: 'bg-green-100 text-green-800',
  bozza: 'bg-slate-100 text-slate-800',
  sospeso: 'bg-yellow-100 text-yellow-800',
  cessato: 'bg-red-100 text-red-800',
  da_completare: 'bg-orange-100 text-orange-800'
};

const statusLabels = {
  attivo: 'Attivo',
  bozza: 'Bozza',
  sospeso: 'Sospeso',
  cessato: 'Cessato',
  da_completare: 'Da Completare'
};

export default function RegistroVolontari() {
  const [volontari, setVolontari] = useState([]);
  const [ente, setEnte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [ordinamento, setOrdinamento] = useState('progressivo');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [volontariRes, enteRes] = await Promise.all([
        fetch(`${API_URL}/api/volontari`, { credentials: 'include' }),
        fetch(`${API_URL}/api/ente`, { credentials: 'include' })
      ]);

      if (volontariRes.ok) {
        const data = await volontariRes.json();
        // Filter to show only active and relevant volunteers
        const registroVolontari = data.filter(v => 
          ['attivo', 'bozza', 'da_completare'].includes(v.stato)
        );
        setVolontari(registroVolontari);
      }

      if (enteRes.ok) {
        setEnte(await enteRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/registro-volontari?ordine=${ordinamento}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `registro_volontari_${ordinamento}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        const ordineLabel = {alfabetico: 'Alfabetico', progressivo: 'Progressivo', data: 'Data'}[ordinamento];
        toast.success(`Registro PDF generato (${ordineLabel})`);
      } else {
        toast.error('Errore durante la generazione del PDF');
      }
    } catch (error) {
      toast.error('Errore durante la generazione del PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="registro-volontari-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Registro Volontari</h1>
          <p className="page-subtitle">Visualizza e genera il registro dei volontari</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-slate-500" />
            <Select value={ordinamento} onValueChange={setOrdinamento}>
              <SelectTrigger className="w-[160px]" data-testid="select-ordinamento">
                <SelectValue placeholder="Ordinamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alfabetico">Alfabetico (A-Z)</SelectItem>
                <SelectItem value="progressivo">Progressivo</SelectItem>
                <SelectItem value="data">Data Iscrizione</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={handlePrint} data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Stampa
          </Button>
          <Button 
            className="btn-primary" 
            onClick={handleDownloadPDF}
            disabled={downloading}
            data-testid="download-pdf-btn"
          >
            {downloading ? (
              <span className="spinner mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Genera PDF
          </Button>
        </div>
      </div>

      {/* Ente Header (for print) */}
      <Card className="print:shadow-none print:border-none">
        <CardContent className="p-6">
          <div className="text-center border-b border-slate-200 pb-6 mb-6">
            <h2 className="text-2xl font-bold text-slate-900">
              {ente?.denominazione || 'Nome Ente'}
            </h2>
            <p className="text-slate-500 mt-1">REGISTRO DEI VOLONTARI</p>
            {ente?.codice_fiscale && (
              <p className="text-sm text-slate-400 mt-2">
                C.F.: {ente.codice_fiscale}
                {ente?.indirizzo && ` • ${ente.indirizzo}`}
              </p>
            )}
          </div>
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>
              Data generazione: {new Date().toLocaleDateString('it-IT', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
            <span>{volontari.length} volontari nel registro</span>
          </div>
        </CardContent>
      </Card>

      {/* Volontari Table */}
      <Card className="print:shadow-none print:border-none">
        <CardHeader className="print:hidden">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="w-5 h-5 text-[#0055BB]" />
            Elenco Volontari
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {volontari.length === 0 ? (
            <div className="empty-state py-16">
              <Users className="empty-state-icon" />
              <p className="empty-state-title">Nessun volontario nel registro</p>
              <p className="empty-state-text">
                Aggiungi dei volontari per popolare il registro
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">N.</TableHead>
                    <TableHead>Cognome e Nome</TableHead>
                    <TableHead>Codice Fiscale</TableHead>
                    <TableHead>Data Inizio</TableHead>
                    <TableHead>Data Fine</TableHead>
                    <TableHead className="print:hidden">Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {volontari.map((volontario, index) => (
                    <TableRow key={volontario.volontario_id} data-testid={`registro-row-${volontario.volontario_id}`}>
                      <TableCell className="font-medium text-slate-500">
                        {volontario.progressivo || index + 1}
                      </TableCell>
                      <TableCell className="font-medium">
                        {volontario.cognome} {volontario.nome}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {volontario.codice_fiscale || '-'}
                      </TableCell>
                      <TableCell>
                        {volontario.data_inizio_attivita || '-'}
                      </TableCell>
                      <TableCell>
                        {volontario.data_cessazione_attivita || '-'}
                      </TableCell>
                      <TableCell className="print:hidden">
                        <Badge className={statusColors[volontario.stato] || 'bg-slate-100 text-slate-800'}>
                          {statusLabels[volontario.stato] || volontario.stato}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer / Disclaimer */}
      <Card className="bg-slate-50 print:bg-white print:border-t print:border-slate-200 print:shadow-none">
        <CardContent className="p-6">
          <div className="text-sm text-slate-500 space-y-4">
            <p>
              <strong>Disclaimer:</strong> Documento generato dal gestionale interno dell'ente. 
              L'utilizzo ai fini formali deve essere verificato in base alla normativa applicabile 
              e alle procedure adottate dall'ente.
            </p>
            <p>
              Verificare sempre con il proprio consulente legale/amministrativo la conformità 
              delle procedure adottate.
            </p>
            <div className="pt-4 border-t border-slate-200 mt-4 print:block hidden">
              <p className="mb-8">Firma e timbro dell'ente: _________________________________</p>
              <p>Data: _________________</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Print styles info */}
      <div className="text-sm text-slate-400 text-center print:hidden">
        Per una stampa ottimale, usa la funzione "Genera PDF" oppure stampa direttamente questa pagina
      </div>
    </div>
  );
}
